import { createAsyncThunk } from '@reduxjs/toolkit';
import { fetchRankingPagedSequential } from './ranking-paging-runner';
import { pickRankingStrategy, Scope, Domain } from '@flexo/general';

const ENV = (import.meta as any).env;
const LOCALE = (navigator.language.split('-')[0] || ENV?.VITE_APP_LOCALE || 'en').split('-')[0];

export const FetchRankingPagedAsync = createAsyncThunk(
  'kpi/FetchRankingPagedAsync',
  async (data: {
    org: string; scope: Scope; domain: Domain; entityId: string;
    totalSites: number; selectedDate: [Date, Date]; selectedTimeSet: any;
  }) => {
    const strategy = pickRankingStrategy(data.org);
    const merged = await fetchRankingPagedSequential({
      strategy,
      baseUrl: ENV.VITE_APP_BASE_URL,
      scope: data.scope,
      domain: data.domain,
      entityId: data.entityId,
      selectedDate: data.selectedDate,
      selectedTimeSet: data.selectedTimeSet,
      locale: LOCALE,
      totalSites: data.totalSites,
    });
    return {
      ...(merged?.energyRanking ? { energyRanking: merged.energyRanking } : {}),
      ...(merged?.economicsRanking ? { economicsRanking: merged.economicsRanking } : {}),
      ...(merged?.costsRanking ? { costsRanking: merged.costsRanking } : {}),
    } as any;
  }
);

export const FetchRankingUnifiedAsync = createAsyncThunk(
  'kpi/FetchRankingUnifiedAsync',
  async (
    data: {
      org: string; scope: Scope; domain: Domain; entityId: string;
      totalSites?: number; selectedDate: [Date, Date]; selectedTimeSet: any;
      fetchAll: boolean;
    },
    thunkAPI
  ) => {
    const ENV = (import.meta as any).env;
    const LOCALE = (navigator.language.split('-')[0] || ENV?.VITE_APP_LOCALE || 'en').split('-')[0];
    // eslint-disable-next-line no-alert

    // unified path: always use strategy+runner
    const strategy = pickRankingStrategy(data.org);
    const effectiveTotal = data.fetchAll
      ? Math.max(0, data.totalSites || 0)
      : strategy.pageSize; // single page top-5

    const merged = await fetchRankingPagedSequential({
      strategy,
      baseUrl: ENV.VITE_APP_BASE_URL,
      scope: data.scope,
      domain: data.domain,
      entityId: data.entityId,
      selectedDate: data.selectedDate,
      selectedTimeSet: data.selectedTimeSet,
      locale: LOCALE,
      totalSites: effectiveTotal,
    });

    const payload = {
      ...(merged?.energyRanking ? { energyRanking: merged.energyRanking } : {}),
      ...(merged?.economicsRanking ? { economicsRanking: merged.economicsRanking } : {}),
      ...(merged?.costsRanking ? { costsRanking: merged.costsRanking } : {}),
    } as any;

    return { mode: data.fetchAll ? 'paged' : 'top5', payload } as any;
  }
);

// Domain-specific wrappers to keep energy and economics separated at call sites
export const FetchEnergyRankingUnifiedAsync = createAsyncThunk(
  'kpi/FetchEnergyRankingUnifiedAsync',
  async (
    data: {
      org: string; scope: Scope; entityId: string;
      totalSites?: number; selectedDate: [Date, Date]; selectedTimeSet: any;
      fetchAll: boolean;
    },
    thunkAPI
  ) => {
    // Delegate to generic thunk with domain fixed to 'energy'
    return await thunkAPI.dispatch(
      FetchRankingUnifiedAsync({ ...data, domain: 'energy' } as any) as any
    );
  }
);

export const FetchEconomicsRankingUnifiedAsync = createAsyncThunk(
  'kpi/FetchEconomicsRankingUnifiedAsync',
  async (
    data: {
      org: string; scope: Scope; entityId: string;
      totalSites?: number; selectedDate: [Date, Date]; selectedTimeSet: any;
      fetchAll: boolean;
    },
    thunkAPI
  ) => {
    // Delegate to generic thunk with domain fixed to 'economics'
    return await thunkAPI.dispatch(
      FetchRankingUnifiedAsync({ ...data, domain: 'economics' } as any) as any
    );
  }
);


